<template>
  <div class="estimate-summary">
    <FormHeading
      :ordinal="`${stepNumber}.1`"
      :title="$t('estimate.steps.summary.heading')"
    />
    <div class="estimate-summary__section estimate-summary__section--top">
      <SummaryField
        v-for="{ cost, id } in summary"
        :key="id"
        :label="$t(`estimateSummary.estimateTypes.${id}`)"
        :value="cost"
      />
    </div>
    <FormHeading
      :ordinal="`${stepNumber}.2`"
      :title="$t('estimate.steps.summary.heading-2')"
    />
    <div class="estimate-summary__section estimate-summary__section--top">
      <SummaryField
        v-for="{ time, id } in summary"
        :key="id"
        :label="$t(`estimateSummary.multipliers.${id}`)"
        :value="time"
      />
    </div>
    <FormHeading
      :ordinal="`${stepNumber}.3`"
      :title="$t('estimate.steps.summary.heading-3')"
    />
    <div class="estimate-summary__section estimate-summary__section--bottom">
      <DatePicker
        name="cleaning_date"
        :placeholder="$t('estimateSummary.fieldPlaceholders.date')"
        :label="$t('estimateSummary.fieldLabels.cleaning_date')"
        :initialValue="initialValues.cleaningDate"
      />
      <TimePicker
        name="cleaning_time"
        :placeholder="$t('estimateSummary.fieldPlaceholders.time')"
        :label="$t('estimateSummary.fieldLabels.cleaning_time')"
        :initialValue="initialValues.cleaningTime"
      />
      <Select
        name="payment_method"
        :options="paymentOptions"
        :placeholder="$t('estimateSummary.fieldPlaceholders.payment_method')"
        :label="$t('estimateSummary.fieldLabels.payment_method')"
        :initialValue="initialValues.paymentMethod"
      />
      <Input
        name="entrance_method"
        :placeholder="$t('estimateSummary.fieldPlaceholders.entrance_method')"
        :label="$t('estimateSummary.fieldLabels.entrance_method')"
        :initialValue="initialValues.entranceMethod"
      />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import FormHeading from '@/components/FormHeading';
import { formatTime } from '@/common/helpers';
import {
  SummaryField,
  DatePicker,
  TimePicker,
  Select,
  Input,
} from '@/components/common';

const { mapState } = createNamespacedHelpers('estimate');

// For now we only support USD
const currencyFormatter = Intl
  .NumberFormat('en-US', { style: 'currency', currency: 'USD' })
  .format;

export default {
  name: 'EstimateSummary',
  components: {
    SummaryField,
    FormHeading,
    DatePicker,
    TimePicker,
    Select,
    Input,
  },
  props: {
    stepNumber: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      paymentOptions: [
        { label: this.$t('estimateSummary.paymentOptions.card'), value: 'card' },
        { label: this.$t('estimateSummary.paymentOptions.check'), value: 'check' },
        { label: this.$t('estimateSummary.paymentOptions.cash'), value: 'cash' },
        { label: this.$t('estimateSummary.paymentOptions.other'), value: 'other' },
      ],
    };
  },
  computed: {
    ...mapState({
      summary: state => Object.keys(state.current.data.costs).map((key) => {
        const { dollars, cents, time } = state.current.data.costs[key];
        const cost = currencyFormatter(parseFloat(`${dollars}.${cents}`));

        return {
          id: key,
          cost,
          time: formatTime(time),
        };
      }),
      initialValues: (state) => {
        const { current: { data } } = state;
        return {
          cleaningDate: data.cleaning_date,
          cleaningTime: data.cleaning_time,
          entranceMethod: data.entrance_method,
          paymentMethod: data.payment_method,
        };
      },
    }),
  },
};
</script>

<style lang="scss" scoped>
  @mixin sectionLayout {
    @include md-up {
      &:nth-child(-n+3) {
        flex: 0 0 calc(33% - 14px);
      }

      &:nth-child(n+4) {
        flex: 0 0 calc(25% - 18px);
      }
    }
  }

  .estimate-summary {
    @include md-up {
      margin: 0 -100px;
    }

    &__section {
      width: 100%;

      @include md-up {
        display: flex;
        flex-flow: row wrap;
        justify-content: space-between;
      }

      &--top {
        border-bottom: 1px solid $grayLight;
        margin-bottom: 24px;
        padding-bottom: 20px;

        @include md-up {
          margin-bottom: 64px;
          padding-bottom: 28px;
        }

        .summary-field {
          @include sectionLayout();
        }
      }

      &--bottom {
        @include md-up {
          width: 580px;
          margin: 0 auto;
        }
        >div {
          flex: 0 0 calc(50% - 20px);
        }
      }
    }
  }
</style>
